import type { Editor } from '@tiptap/core'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import styled, { useTheme } from 'styled-components'

import Icon from '../../general/Icon'
import { Dropdown } from '../../navigation/Dropdown/Dropdown'

export type BlockStyle =
  | 'paragraph'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'bulletList'
  | 'orderedList'

type DropdownValue = BlockStyle

type BlockStyleOption = {
  value: DropdownValue
  badge: ReactNode
  label: string
}

/** Declared before `OPTIONS` so Biome allows JSX in the options list. */
const HeadingBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme }) => theme.color.neutral.color5};
`

const OPTIONS: BlockStyleOption[] = [
  {
    value: 'paragraph',
    badge: <Icon name="AlignLeftOutlined" size={14} />,
    label: 'Normal'
  },
  {
    value: 'h1',
    badge: <HeadingBadge>H1</HeadingBadge>,
    label: 'Heading 1'
  },
  {
    value: 'h2',
    badge: <HeadingBadge>H2</HeadingBadge>,
    label: 'Heading 2'
  },
  {
    value: 'h3',
    badge: <HeadingBadge>H3</HeadingBadge>,
    label: 'Heading 3'
  },
  {
    value: 'bulletList',
    badge: <Icon name="UnorderedListOutlined" size={14} />,
    label: 'Bullet List'
  },
  {
    value: 'orderedList',
    badge: <Icon name="OrderedListOutlined" size={14} />,
    label: 'Numbered List'
  }
]

function getCurrentValue(editor: Editor): DropdownValue {
  if (editor.isActive('bulletList')) {
    return 'bulletList'
  }
  if (editor.isActive('orderedList')) {
    return 'orderedList'
  }
  if (editor.isActive('heading', { level: 1 })) {
    return 'h1'
  }
  if (editor.isActive('heading', { level: 2 })) {
    return 'h2'
  }
  if (editor.isActive('heading', { level: 3 })) {
    return 'h3'
  }
  return 'paragraph'
}

function applyValue(editor: Editor, value: DropdownValue) {
  switch (value) {
    case 'paragraph': {
      let chain = editor.chain().focus()
      if (editor.isActive('bulletList')) {
        chain = chain.toggleBulletList()
      }
      if (editor.isActive('orderedList')) {
        chain = chain.toggleOrderedList()
      }
      chain.setParagraph().run()
      break
    }
    case 'h1':
    case 'h2':
    case 'h3': {
      const level = Number(value.slice(1)) as 1 | 2 | 3
      let chain = editor.chain().focus()
      if (editor.isActive('bulletList')) {
        chain = chain.toggleBulletList()
      }
      if (editor.isActive('orderedList')) {
        chain = chain.toggleOrderedList()
      }
      chain.setHeading({ level }).run()
      break
    }
    case 'bulletList': {
      if (editor.isActive('bulletList')) {
        return
      }
      let chain = editor.chain().focus()
      if (editor.isActive('orderedList')) {
        chain = chain.toggleOrderedList()
      }
      chain.toggleBulletList().run()
      break
    }
    case 'orderedList': {
      if (editor.isActive('orderedList')) {
        return
      }
      let chain = editor.chain().focus()
      if (editor.isActive('bulletList')) {
        chain = chain.toggleBulletList()
      }
      chain.toggleOrderedList().run()
      break
    }
    default: {
      break
    }
  }
}

export type BlockStyleDropdownProps = {
  editor: Editor
}

export function BlockStyleDropdown({ editor }: BlockStyleDropdownProps) {
  const theme = useTheme()
  const [current, setCurrent] = useState<DropdownValue>(() =>
    getCurrentValue(editor)
  )

  useEffect(() => {
    const handler = () => {
      setCurrent(getCurrentValue(editor))
    }
    editor.on('transaction', handler)
    editor.on('selectionUpdate', handler)
    return () => {
      editor.off('transaction', handler)
      editor.off('selectionUpdate', handler)
    }
  }, [editor])

  const triggerLabel = useMemo(() => {
    const opt = OPTIONS.find((o) => o.value === current)
    return opt?.label ?? 'Normal'
  }, [current])

  const selectedBg = theme.color.status.secondary

  const [menuOpen, setMenuOpen] = useState(false)

  const handleSelect = useCallback(
    (value: DropdownValue) => {
      applyValue(editor, value)
      setCurrent(getCurrentValue(editor))
      setMenuOpen(false)
    },
    [editor]
  )

  return (
    <Dropdown
      open={menuOpen}
      onOpenChange={setMenuOpen}
      trigger={['click']}
      placement="bottomLeft"
      overlayStyle={{ minWidth: 220 }}
      overlay={
        <MenuPanel role="menu" aria-label="Block style">
          {OPTIONS.map((opt) => (
            <MenuRow
              key={opt.value}
              type="button"
              role="menuitem"
              $selected={current === opt.value}
              $selectedBg={selectedBg}
              onClick={() => {
                handleSelect(opt.value)
              }}
            >
              <BadgeCell>{opt.badge}</BadgeCell>
              <LabelCell>{opt.label}</LabelCell>
            </MenuRow>
          ))}
        </MenuPanel>
      }
    >
      <TriggerButton type="button">
        <span>{triggerLabel}</span>
        <Icon name="DownOutlined" size={10} />
      </TriggerButton>
    </Dropdown>
  )
}

const MenuPanel = styled.div`
  background: ${({ theme }) => theme.color.neutral.color11};
  border-radius: ${({ theme }) => theme.properties.borderRadius.small};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  padding: 4px;
  min-width: 220px;
`

const MenuRow = styled.button<{ $selected: boolean; $selectedBg: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  background: ${({ $selected, $selectedBg }) =>
    $selected ? $selectedBg : 'transparent'};
  color: ${({ theme }) => theme.color.neutral.color3};

  &:hover {
    background: ${({ $selected, $selectedBg, theme }) =>
      $selected ? $selectedBg : theme.color.neutral.color8};
  }
`

const BadgeCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.neutral.color5};
`

const LabelCell = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 400;
`

const TriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.color.neutral.color7};
  background: ${({ theme }) => theme.color.neutral.color11};
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.color.neutral.color6};
    background: ${({ theme }) => theme.color.neutral.color8};
  }
`
